import { Box, Typography } from "@material-ui/core"
import { ApolloError } from "apollo-boost"
import { ErrorResponse } from "apollo-link-error"
import { Router as RouterProps, withRouter } from "next/router"
import * as React from "react"
import { compose } from "react-apollo"
import ReactDiv100vh from "react-div-100vh"
import { ResponseErrorType } from "../../graphql/generated-types"
import { getBasePath, importRouter } from "../../utils/router"
import { getApolloError, hasSpecificApolloError } from "../setup/apollo/utils"
import BigButton from "./BigButton"
import BigErrorIcon from "./bigIcons/BigErrorIcon"
import { Trans } from "./Trans"

const Router = importRouter()

interface Props {
    router: RouterProps
    apolloError?: ApolloError | ErrorResponse
}

const ErrorWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <ReactDiv100vh>
        <Box
            display="flex"
            flexDirection="column"
            minHeight="100%"
            width="100%"
            maxWidth="800px"
            textAlign="center"
            justifyContent="center"
            marginX="auto"
            paddingX={2}
            paddingBottom={6}>
            {children}
        </Box>
    </ReactDiv100vh>
)

class FullPageError extends React.Component<Props> {
    static defaultProps = {
        fullRound: true,
        className: "",
        style: {},
    }

    renderGeneral = () => (
        <ErrorWrapper>
            <Box>
                <BigErrorIcon />
                <Typography variant="h6">Something technical went wrong.</Typography>
                <Typography style={{ marginTop: "1rem" }} variant="body1">
                    <b>
                        <Trans name="We were automatically alerted about this." />{" "}
                    </b>{" "}
                    <Trans name="Please try again or get in touch with us directly." />
                </Typography>
            </Box>
            <Box marginTop={4}>
                <BigButton
                    fullRound={true}
                    fullWidth={true}
                    onClick={() => location.reload()}
                    style={{
                        width: "100%",
                        maxWidth: "300px",
                    }}>
                    <Trans name="Reload & Try again" />
                </BigButton>
            </Box>
        </ErrorWrapper>
    )

    showLocationsButton = (errorType: ResponseErrorType) => {
        return errorType === ResponseErrorType.LocationNotFound
    }

    renderNotFound = (errorType: ResponseErrorType) => {
        const showLocationsButton = this.showLocationsButton(errorType)

        return (
            <ErrorWrapper>
                <Box>
                    <BigErrorIcon />
                    <Typography variant="h6" style={{ marginBottom: "1rem" }}>
                        <Trans name="Oh no! We can’t seem to find the ordering page you’re looking for!" />
                    </Typography>
                    {showLocationsButton ? (
                        <Typography variant="body1">
                            <Trans name="You might have typed in the wrong address or the page has moved. In the meantime, try again or try ordering from different location." />
                        </Typography>
                    ) : (
                        <Typography variant="body1">
                            <Trans name="You might have typed in the wrong address or the page has moved. In the meantime, try again or go back to previous page." />
                        </Typography>
                    )}
                </Box>
                {showLocationsButton && (
                    <Box marginTop={4}>
                        <BigButton
                            fullRound={true}
                            fullWidth={true}
                            onClick={() => Router.pushRoute(getBasePath())}
                            style={{
                                width: "100%",
                                maxWidth: "300px",
                            }}>
                            <Trans name="Order from different location" />
                        </BigButton>
                    </Box>
                )}
            </ErrorWrapper>
        )
    }

    renderNotActiveError = (showLocationsButton: boolean) => {
        return (
            <ErrorWrapper>
                <Box>
                    <BigErrorIcon />
                    <Typography variant="h6" style={{ marginBottom: "1rem" }}>
                        <Trans name="Oh no! The ordering page you’re trying to reach is currently not available." />
                    </Typography>
                    {showLocationsButton ? (
                        <Typography variant="body1">
                            <Trans name="In the meantime, try again or try ordering from different location." />
                        </Typography>
                    ) : (
                        <Typography variant="body1">
                            <Trans name="In the meantime, try again or go back to previous page." />
                        </Typography>
                    )}
                </Box>
                {showLocationsButton && (
                    <Box marginTop={4}>
                        <BigButton
                            fullRound={true}
                            fullWidth={true}
                            onClick={() => Router.pushRoute(getBasePath())}
                            style={{
                                width: "100%",
                                maxWidth: "300px",
                            }}>
                            <Trans name="Order from different location" />
                        </BigButton>
                    </Box>
                )}
            </ErrorWrapper>
        )
    }

    renderNoServicesToDisplayError = () => {
        return (
            <ErrorWrapper>
                <Box>
                    <BigErrorIcon />
                    <Typography variant="h6" style={{ marginBottom: "1rem" }}>
                        <Trans name="Oh no! The page you’re trying to reach is currently not available." />
                    </Typography>
                    <Typography variant="body1">
                        <Trans name="In the meantime, try again or go back to previous page." />
                    </Typography>
                </Box>
            </ErrorWrapper>
        )
    }

    render() {
        const { apolloError } = this.props
        if (apolloError) {
            if (hasSpecificApolloError(apolloError, ResponseErrorType.LocationNotFound)) {
                return this.renderNotFound(ResponseErrorType.LocationNotFound)
            } else if (hasSpecificApolloError(apolloError, ResponseErrorType.AccountNotFound)) {
                return this.renderNotFound(ResponseErrorType.AccountNotFound)
            } else if (hasSpecificApolloError(apolloError, ResponseErrorType.ServiceNotActive)) {
                const errorData = getApolloError(apolloError, ResponseErrorType.ServiceNotActive)
                const hasMultipleLocations =
                    errorData && errorData.extensions ? errorData.extensions.hasMultipleLocations : false
                return this.renderNotActiveError(hasMultipleLocations)
            } else if (hasSpecificApolloError(apolloError, ResponseErrorType.NoLocationPickerServices)) {
                return this.renderNoServicesToDisplayError()
            }
        }

        return this.renderGeneral()
    }
}

export default compose(withRouter)(FullPageError)
